import { Chip, TextField, Theme } from '@mui/material';
import { SyntheticEvent, useEffect, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { unique } from '@synergy/utils';
import { useIntl } from 'react-intl';
import Autocomplete from '@mui/lab/Autocomplete';

// The same two sizes as the shared chip autocomplete: the theme says how big
// a chip is, and the field that holds them follows.
const LEGACY = {
    chipHeight: 32,
    chipPadding: 8,
    gap: '8px',
    inset: '12px 52px 12px 24px',
    minHeight: 52,
    buttonTop: 7,
};
const DENSE = { chipHeight: 19, chipPadding: 2, gap: '4px', inset: '4px 34px 4px 9px', minHeight: 29, buttonTop: 0 };

const useStyles = makeStyles((theme: Theme) => {
    const chip = theme.chip;
    const dense = chip !== undefined && chip.labelMinWidth === 0;
    const size = dense ? DENSE : LEGACY;

    return {
        chip: {
            fontSize: chip?.fontSize ?? 12,
            fontWeight: chip?.fontWeight ?? 'bold',
            height: size.chipHeight,
            padding: size.chipPadding,
            borderRadius: chip?.borderRadius ?? 18,

            '& svg': {
                color: 'rgba(255, 255, 255, 0.7)',
                margin: 0,
                marginRight: 4,
            },
            '& > span': {
                padding: dense ? '0 4px' : '0 8px',
                textAlign: 'center',
            },
        },
        inputContainer: {
            padding: size.inset,
            minHeight: size.minHeight,
            flexWrap: 'wrap',
            gap: size.gap,

            '& button': {
                marginRight: dense ? 0 : 4,
                padding: dense ? 3 : 12,
                marginTop: size.buttonTop,
            },
        },
        input: {
            height: size.chipHeight,
            padding: 0,
            flexGrow: 1,
            textOverflow: 'ellipsis', // fix placeholder ellipsis for chromium based browsers (FF working)
            minWidth: 30,
            width: 0,

            '& + div': {
                top: dense ? 'calc(50% - 14px)' : 'calc(50% - 30px)',
            },
        },
    };
});

export interface ChipTextFieldProps {
    value?: string[];
    name: string;
    placeholderId?: string;
    limitTags?: number;
    onChange?: (values: string[]) => void;
}

export const ChipTextField = (props: ChipTextFieldProps) => {
    const classes = useStyles();
    const intl = useIntl();
    const [values, setValues] = useState<string[]>([]);

    useEffect(() => {
        setValues(props.value ?? []);
    }, [props.value]);

    const handleChanged = (_event: SyntheticEvent<Element, Event>, value: string[]) => {
        const nextValues = value.reduce<string[]>((result, currentValue) => {
            return unique([
                ...result,
                ...currentValue
                    .split(';')
                    .map(splitValue => splitValue.trim())
                    .filter(splitValue => !!splitValue),
            ]);
        }, []);
        if (props.onChange) {
            props.onChange(nextValues);
        } else {
            setValues(nextValues);
        }
    };

    return (
        <Autocomplete
            freeSolo={true}
            multiple={true}
            limitTags={props.limitTags}
            forcePopupIcon={false}
            filterSelectedOptions={true}
            filterOptions={() => []}
            options={['']}
            value={values}
            renderTags={(value: string[], getProps) =>
                value.map((option: string, index: number) => (
                    <Chip {...getProps({ index })} color={'primary'} label={option} className={classes.chip} />
                ))
            }
            renderInput={params => (
                <TextField
                    {...params}
                    name={props.name}
                    InputProps={{
                        ...params.InputProps,
                        className: classes.inputContainer,
                    }}
                    inputProps={{
                        ...params.inputProps,
                        className: classes.input,
                    }}
                    placeholder={props.placeholderId && intl.formatMessage({ id: props.placeholderId })}
                />
            )}
            onChange={handleChanged}
        />
    );
};
